import FingerprintJS from '@fingerprintjs/fingerprintjs';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { MAIN_URL } from '../config';
import { hooks } from '../hooks';
import { Routes } from '../routes';

// Function to detect time of day based on current time
const getTimeOfDay = (): string => {
  const hour = new Date().getHours();
  
  if (hour >= 6 && hour < 11) {
    return "Breakfast";
  } else if (hour >= 11 && hour < 15) {
    return "Lunch";
  } else if (hour >= 15 && hour < 19) {
    return "Evening Snacks";
  } else if (hour >= 19 && hour < 23) {
    return "Dinner";
  } else {
    return "Late Night";
  }
};

const questionData = [
  {
    id: 2,
    question: "What type of cuisine are you in the mood for?",
    options: ["North Indian", "South Indian", "Chinese", "Continental", "Street Food", "I'm not sure"],
    icon: "🌶️"
  },
  {
    id: 3,
    question: "What are your dietary preferences?",
    options: ["Pure Vegetarian", "Jain Food", "Non-Vegetarian", "Vegan", "No restrictions"],
    icon: "🥗"
  },
  {
    id: 4,
    question: "What's your hunger level right now?",
    options: ["Light snack", "Regular meal", "Very hungry", "Family feast", "Just tasting"],
    icon: "😋"
  },
  {
    id: 7,
    icon: "�"
  },
  {
    id: 5,
    question: "How many people are dining?",
    options: ["Just me", "Couple (2)", "Small group (3-4)", "Family (5-6)", "Large group (7+)"],
    icon: "👥"
  },
  {
    id: 6,
    question: "Do you want to try something new today?",
    options: ["Yes, surprise me!", "My usual favorites", "Chef's recommendations", "Trending dishes"],
    icon: "🎯"
  },
];

const QuestionFlow = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fingerprint, setFingerprint] = useState<string>('');
  const [errorEnc, setErrorEnc] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    FingerprintJS.load()
      .then(fp => fp.get())
      .then(result => {
        const visitorId = result.visitorId;
        setFingerprint(visitorId);
      })
      .catch(error => console.error('Error generating'));
  }, []);
  
  const navigate = hooks.useNavigate();

  const handleOptionClick = (option: string) => {
    setAnswers({ ...answers, [currentStep]: option });
  };

  const handleNext = async () => {
    const validQuestions = questionData.filter((q) => q.question && q.options);
    if (currentStep < validQuestions.length - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsTransitioning(false);
      }, 150);
      return;
    }
  
    // Final step: submit
    // Include the auto-detected time of day question at the beginning
    const timeOfDayAnswer = getTimeOfDay();
    const timeOfDayQuestion = {
      question: "What time of day are you dining?",
      answer: timeOfDayAnswer,
    };
    
    const formattedAnswers = [
      timeOfDayQuestion,
      ...questionData
        .filter((q) => q.question && q.options) // Filter out any invalid questions
        .map((q, index) => ({
          question: q.question,
          answer: answers[index],
        }))
    ];
  
    try {
      setIsSubmitting(true);
  
      const restroId = localStorage.getItem("REACT_APP_RESTAURANT_ID");
      const response = await axios.post(
        `${MAIN_URL}admin/preferences?restroId=${restroId}`,
        { answers: formattedAnswers,
          dynamicId:localStorage.getItem("TABLE_ID"),
          auth:fingerprint
         }
      );
  
      if (response?.data?.success) {
        navigate('/menu-list-ai',{state: {
          recommendedIds: response?.data?.ids,
          recommendedDishes: response?.data.recommendedDishes, 
        },});
      } else {
        setErrorEnc(true)
      }
    } catch (error) {
      //console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsTransitioning(false);
      }, 150);
    }
  };

  const handleClose = () => {
    console.log('Close button clicked!'); // Debug log
    
    // Simple test to confirm button works
    // alert('Close button working!'); // Uncomment this line to test
    
    try {
      console.log('Attempting navigation to TabNavigator');
      navigate(Routes.TabNavigator, { replace: true });
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback: try navigating to home
      try {
        console.log('Trying fallback navigation to Menu');
        navigate(Routes.Menu);
      } catch (fallbackError) {
        console.error('Fallback navigation error:', fallbackError);
        // Last resort: go back in history
        console.log('Using window.history.back()');
        window.history.back();
      }
    }
  };

  // Filter out invalid questions (those without a question property and options)
  const validQuestions = questionData.filter((q) => q.question && q.options);
  const question = validQuestions[currentStep];
  const selectedAnswer = answers[currentStep];

  // Safety check: if no question found, return null or redirect
  if (!question || !question.options) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-cyan-200/30 to-blue-300/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-200/30 to-purple-300/30 rounded-full blur-3xl"></div>
      </div>

      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 z-50 p-3 rounded-full bg-white/90 backdrop-blur-sm shadow-lg hover:bg-white hover:scale-110 transition-all duration-200 group cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
        style={{ zIndex: 9999 }}
        aria-label="Close question flow"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          className="text-gray-600 group-hover:text-red-500 transition-colors"
        >
          <path fill="currentColor" d="M6.4 19L5 17.6l5.6-5.6L5 6.4L6.4 5l5.6 5.6L17.6 5L19 6.4L13.4 12l5.6 5.6l-1.4 1.4l-5.6-5.6z"/>
        </svg>
      </button>

      {/* Main container */}
      <div className="flex items-center justify-center min-h-screen px-4 py-8 md:py-12 relative z-10">
        <div className="w-full max-w-3xl">
          {/* Progress bar */}
          <div className="mb-8 px-2">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span className="font-medium">Progress</span>
              <span>{currentStep + 1} of {validQuestions.length}</span>
            </div>
            <div className="w-full bg-gray-200/60 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((currentStep + 1) / validQuestions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Main card */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl md:rounded-3xl shadow-2xl border border-white/20 p-6 md:p-8 lg:p-10 transition-all duration-300 hover:shadow-3xl">
            {/* Error message */}
            {errorEnc && (
              <div role="alert" className="mb-6 border-l-4 border-red-500 bg-red-50/80 backdrop-blur-sm p-4 rounded-r-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-6 h-6 text-red-500"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-800 text-lg">Something went wrong</h3>
                    <p className="mt-1 text-red-700">Please try again later...</p>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => navigate(Routes.TabNavigator, { replace: true })}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-sm font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                      >
                        Explore Menu
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Question header */}
            <div className={`text-center mb-8 transition-all duration-300 ${isTransitioning ? 'opacity-0 transform translate-y-2' : 'opacity-100 transform translate-y-0'}`}>
              <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full mb-4">
                <span className="text-2xl">{question.icon}</span>
                <span className="text-sm font-medium text-cyan-700">
                  Question {currentStep + 1} of {validQuestions.length}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 leading-tight px-4">
                {question.question}
              </h2>
            </div>

            {/* Options */}
            <div className={`space-y-3 mb-8 transition-all duration-300 ${isTransitioning ? 'opacity-0 transform translate-y-4' : 'opacity-100 transform translate-y-0'}`}>
              {question.options && question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  className={`group w-full text-left px-6 py-4 md:py-5 rounded-2xl border-2 transition-all duration-300 shadow-sm hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-cyan-200/50 transform hover:scale-[1.02] ${
                    selectedAnswer === option
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold border-transparent shadow-lg scale-[1.02]'
                      : 'bg-white/70 backdrop-blur-sm hover:bg-white text-gray-700 border-gray-200/60 hover:border-cyan-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-base md:text-lg leading-relaxed pr-4">{option}</span>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 transition-all duration-200 ${
                      selectedAnswer === option
                        ? 'bg-white border-white'
                        : 'border-gray-300 group-hover:border-cyan-400'
                    }`}>
                      {selectedAnswer === option && (
                        <div className="w-full h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200/60">
              {currentStep > 0 ? (
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 px-4 py-2 text-cyan-600 hover:text-cyan-700 font-medium rounded-xl hover:bg-cyan-50 transition-all duration-200 group"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="transform transition-transform group-hover:-translate-x-1">
                    <path fill="currentColor" d="M11.53 6.47a.75.75 0 0 1 0 1.06l-3.72 3.72H18a.75.75 0 0 1 0 1.5H7.81l3.72 3.72a.75.75 0 1 1-1.06 1.06l-5-5a.75.75 0 0 1 0-1.06l5-5a.75.75 0 0 1 1.06 0" />
                  </svg>
                  <span className="hidden sm:inline">Back</span>
                </button>
              ) : (
                <div></div>
              )}

              <button
                onClick={handleNext}
                disabled={!selectedAnswer || isSubmitting}
                className={`group inline-flex items-center justify-center gap-3 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-cyan-200/50 ${
                  !selectedAnswer || isSubmitting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none transform-none'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-cyan-200/50 hover:shadow-xl'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="relative">
                      <img
                        src="/loading-dishes.gif"
                        alt="Loading"
                        className="w-6 h-6 md:w-8 md:h-8 rounded-full"
                      />
                    </div>
                    <span className="hidden sm:inline text-sm md:text-base">Finding perfect dishes...</span>
                  </>
                ) : (
                  <>
                    <span className="text-sm md:text-base">
                      {currentStep === validQuestions.length - 1 ? 'Get My Recommendations' : 'Next Question'}
                    </span>
                    {!isSubmitting && (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        className="transform transition-transform group-hover:translate-x-1"
                      >
                        <path fill="currentColor" d="M12.47 17.53a.75.75 0 0 1 0-1.06l3.72-3.72H6a.75.75 0 0 1 0-1.5h10.19l-3.72-3.72a.75.75 0 1 1 1.06-1.06l5 5a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 0 1-1.06 0" />
                      </svg>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionFlow;
